import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from 'recharts';
import { Users, Activity, AlertTriangle, Star } from 'lucide-react';

import StatCard from './components/StatCard';
import ChartCard from './components/ChartCard';
import DataTable, { RatingCell } from './components/DataTable';
import { downloadChartPNG, DownloadBtn } from './components/downloadUtil';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const EMPTY_STATS = {
  newUsersThisWeek: { label: 'New Users This Week', value: 0, change: 0 },
  queriesToday: { label: 'Queries Today', value: 0, change: 0 },
  usersAtZeroCredits: { label: 'Users at 0 Credits', value: 0, change: 0 },
  lowRatingReviews: { label: 'Low-Rating Reviews (1-2★)', value: 0, change: 0 },
};

// ── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl border text-xs px-3 py-2 shadow-lg"
      style={{
        background:   isDark ? '#0f1729' : '#fff',
        borderColor:  isDark ? '#1e2d52' : '#e2e8f0',
        color:        isDark ? '#c9d1d9' : '#374151',
      }}
    >
      {label && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#F87217' }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const AdminDashboard = ({ isDark, token }) => {
  const gridColor = isDark ? 'rgba(100,160,255,0.07)' : '#f1f5f9';
  const axisColor = isDark ? '#3d4f7a'                : '#cbd5e1';
  const textMuted = isDark ? '#8b949e'                : '#64748b';
  // ── Chart refs ──
  const dailyQueryRef = useRef(null);
  const customAgentsRef = useRef(null);
  const agentsRatingRef = useRef(null);
  const creditExhaustionRef = useRef(null);
  const lowRatingRef = useRef(null);

  // Live API state (no mock fallback)
  const [adminStats, setAdminStats] = useState(EMPTY_STATS);
  const [dailyQueryVolume, setDailyQueryVolume] = useState([]);
  const [agentsByRating, setAgentsByRating] = useState([]);
  // const [customAgentsPerMonth, setCustomAgentsPerMonth] = useState([]);
  const [creditExhaustionDistribution, setCreditExhaustionDistribution] = useState([]);
  const [lowRatingReviews, setLowRatingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentGranularity, setAgentGranularity] = useState('monthly'); // 'daily'|'weekly'|'monthly'
  const [customAgentsData, setCustomAgentsData] = useState([]);

  const fetchCustomAgentsMonthly = useCallback(async () => {
    const { data } = await axios.get(`${API}/custom-agents-monthly`, { headers: { Authorization: `Bearer ${token}` } });
    setCustomAgentsData(Array.isArray(data) ? data : []);
  }, [token]);

  const fetchCustomAgentsDaily = useCallback(async () => {
    const { data } = await axios.get(`${API}/custom-agents-daily`, { headers: { Authorization: `Bearer ${token}` } });
    setCustomAgentsData(Array.isArray(data) ? data : []);
  }, [token]);

  const fetchCustomAgentsWeekly = useCallback(async () => {
    const { data } = await axios.get(`${API}/custom-agents-weekly`, { headers: { Authorization: `Bearer ${token}` } });
    setCustomAgentsData(Array.isArray(data) ? data : []);
  }, [token]);

// fetch based on granularity
  useEffect(() => {
  if (!token) return;
  if (agentGranularity === 'daily') fetchCustomAgentsDaily();
  else if (agentGranularity === 'weekly') fetchCustomAgentsWeekly();
  else fetchCustomAgentsMonthly();
}, [agentGranularity, token, fetchCustomAgentsDaily, fetchCustomAgentsWeekly, fetchCustomAgentsMonthly]);

  // ── Single loading-key state (only one download at a time) ──
  const [dlKey, setDlKey] = useState('');
  const dl = (ref, key, file) => downloadChartPNG(ref, file, isDark, (v) => setDlKey(v ? key : ''));

  const fetchAll = useCallback(async () => {
    if (!token) {
      setError('Missing authentication token. Please sign in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const headers = { Authorization: `Bearer ${token}` };
    const calls = [
      ['stats', axios.get(`${API}/stats`, { headers })],
      ['daily', axios.get(`${API}/daily-queries`, { headers })],
      ['ratings', axios.get(`${API}/agents-rating`, { headers })],
      ['custom', axios.get(`${API}/custom-agents-monthly`, { headers })],
      ['credit', axios.get(`${API}/credit-distribution`, { headers })],
      ['reviews', axios.get(`${API}/low-reviews`, { headers })],
    ];

    const results = await Promise.allSettled(calls.map(([, req]) => req));
    const failed = [];

    results.forEach((result, index) => {
      const key = calls[index][0];
      if (result.status === 'rejected') {
        failed.push(result.reason);
        return;
      }

      const data = result.value?.data;
      if (key === 'stats') setAdminStats(data || EMPTY_STATS);
      if (key === 'daily') setDailyQueryVolume(Array.isArray(data) ? data : []);
      if (key === 'ratings') setAgentsByRating(Array.isArray(data) ? data : []);
      if (key === 'custom') setCustomAgentsData(Array.isArray(data) ? data : []);
      if (key === 'credit') setCreditExhaustionDistribution(Array.isArray(data) ? data : []);
      if (key === 'reviews') setLowRatingReviews(Array.isArray(data) ? data : []);
    });

    if (failed.length) {
      const status = failed[0]?.response?.status;
      if (status === 401) {
        setError('Session expired. Please sign in again.');
      } else if (status === 403) {
        setError('You are authenticated but do not have admin access.');
      } else {
        setError(`Failed to load ${failed.length} admin data request(s).`);
      }
    }

    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Colour bars based on rating ──
  const ratingBarColor = (rating) => {
    if (rating >= 4.5) return '#10b981';
    if (rating >= 4.0) return '#F87217';
    if (rating >= 3.5) return '#f59e0b';
    return '#ef4444';
  };

  // ── Review table columns — isDark-aware ──
  const reviewColumns = [
    { key: 'agent', label: 'Agent' },
    {
      key: 'user', label: 'User',
      render: (val) => (
        <span className="truncate max-w-[160px] block text-xs font-medium"
          style={{ color: isDark ? '#94a3b8' : '#1e293b' }}>
          {val}
        </span>
      ),
    },
    {
      key: 'rating', label: 'Rating',
      render: (val) => <RatingCell rating={val} isDark={isDark} />,
    },
    {
      key: 'comment', label: 'Comment',
      render: (val) => (
        <span className="text-xs line-clamp-2 max-w-[280px] block"
          style={{ color: isDark ? '#8b949e' : '#1f2937' }} title={val}>
          {val}
        </span>
      ),
    },
    { key: 'date', label: 'Date' },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {loading && (
        <div
          className="rounded-xl border px-3 py-2 text-xs"
          style={{
            background: isDark ? '#0f1729' : '#fff',
            borderColor: isDark ? '#1e2d52' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#475569',
          }}
        >
          Loading live admin metrics...
        </div>
      )}
      {!!error && (
        <div
          className="rounded-xl border px-3 py-2 text-xs flex items-center justify-between gap-3"
          style={{
            background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
            borderColor: isDark ? 'rgba(239,68,68,0.35)' : '#fecaca',
            color: isDark ? '#fca5a5' : '#991b1b',
          }}
        >
          <span>{error}</span>
          <button
            onClick={fetchAll}
            className="px-2 py-1 rounded-md text-[11px] font-semibold"
            style={{
              background: isDark ? 'rgba(248,114,23,0.2)' : '#ffedd5',
              color: '#F87217',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── KPI Stat Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label={adminStats.newUsersThisWeek?.label || 'New Users This Week'}
          value={adminStats.newUsersThisWeek?.value ?? 0}
          change={adminStats.newUsersThisWeek?.change ?? 0}
          icon={Users}
          spark={adminStats.newUsersThisWeek?.spark}
          isDark={isDark}
        />
        <StatCard
          label={adminStats.queriesToday?.label || 'Queries Today'}
          value={adminStats.queriesToday?.value ?? 0}
          change={adminStats.queriesToday?.change ?? 0}
          icon={Activity}
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          isLive
          isDark={isDark}
        />
        <StatCard
          label={adminStats.usersAtZeroCredits?.label || 'Users at 0 Credits'}
          value={adminStats.usersAtZeroCredits?.value ?? 0}
          change={adminStats.usersAtZeroCredits?.change ?? 0}
          icon={AlertTriangle}
          isWarning
          isDark={isDark}
        />
        <StatCard
          label={adminStats.lowRatingReviews?.label || 'Low-Rating Reviews (1-2★)'}
          value={adminStats.lowRatingReviews?.value ?? 0}
          change={adminStats.lowRatingReviews?.change ?? 0}
          icon={Star}
          isAlert
          isDark={isDark}
        />
      </div>

      {/* ── Row 1: Daily Queries + Custom Agents ────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Daily Query Volume */}
        <div ref={dailyQueryRef}>
          <ChartCard
            title="Daily Query Volume"
            subtitle="Last 30 days"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'daily'} isDark={isDark} onClick={() => dl(dailyQueryRef, 'daily', 'daily-query-volume')} />}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyQueryVolume} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis dataKey="day"    tick={{ fontSize: 9,  fill: axisColor }} tickLine={false} axisLine={false} interval={0} />
                <YAxis                  tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Bar dataKey="queries" name="Queries" fill="#F87217" radius={[4, 4, 0, 0]} barSize={10}>
                  {dailyQueryVolume.map((_, i) => (
                    <Cell key={i} fill={i === dailyQueryVolume.length - 1 ? '#FF4F00' : '#F87217'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Custom Agents per Month */}
        <div ref={customAgentsRef}>
          <ChartCard
            title="Custom Agents Created"
            subtitle="Last 6 months"
            isDark={isDark}
            action={<div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    {['daily','weekly','monthly'].map(g => (
      <button key={g} onClick={() => setAgentGranularity(g)}
        style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
          background: agentGranularity === g ? '#8b5cf6' : 'transparent',
          color: agentGranularity === g ? '#fff' : axisColor,
          border: `1px solid ${agentGranularity === g ? '#8b5cf6' : axisColor}`,
        }}>
        {g.charAt(0).toUpperCase() + g.slice(1)}
      </button>
    ))}<DownloadBtn loading={dlKey === 'agents'} isDark={isDark} onClick={() => dl(customAgentsRef, 'agents', 'custom-agents-per-month')} /> </div>}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={customAgentsData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
                <YAxis                 tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Bar dataKey="agents" name="Agents" fill="#8b5cf6" radius={[6, 6, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* ── Row 2: Agents by Rating + Credit Exhaustion ─────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Agents Ranked by Avg Rating – vertical scroll */}
        <div ref={agentsRatingRef}>
          <ChartCard
            title="Agents Ranked by Avg Rating"
            subtitle="Current month · colour-coded by score"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'rating'} isDark={isDark} onClick={() => dl(agentsRatingRef, 'rating', 'agents-by-rating')} />}
          >
            <div
              style={{
                maxHeight: 280, overflowY: 'auto', overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
              }}
            >
              <div style={{ minHeight: agentsByRating.length * 36 + 20 }}>
                <ResponsiveContainer width="100%" height={agentsByRating.length * 36 + 20}>
                  <BarChart
                    layout="vertical"
                    data={agentsByRating}
                    margin={{ top: 4, right: 48, bottom: 4, left: 0 }}
                  >
                    <CartesianGrid stroke={gridColor} horizontal={false} />
                    <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="agent" width={165} tick={{ fontSize: 11, fill: textMuted }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Bar dataKey="rating" name="Avg Rating" radius={[0, 6, 6, 0]} barSize={16}>
                      {agentsByRating.map((entry, i) => (
                        <Cell key={i} fill={ratingBarColor(entry.rating)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: Agents</span>
              <span>X-Axis: Avg Rating</span>
            </div>
          </ChartCard>
        </div>

        {/* Credit Exhaustion Distribution – horizontal scroll */}
        <div ref={creditExhaustionRef}>
          <ChartCard
            title="Credit Exhaustion Distribution"
            subtitle="Users grouped by credit usage %"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'credit'} isDark={isDark} onClick={() => dl(creditExhaustionRef, 'credit', 'credit-exhaustion')} />}
          >
            <div style={{
              overflowX: 'auto', overflowY: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
            }}>
              <div style={{ minWidth: Math.max(creditExhaustionDistribution.length * 80, 320) }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={creditExhaustionDistribution} margin={{ top: 4, right: 16, bottom: 8, left: 10 }}>
                    <CartesianGrid stroke={gridColor} vertical={false} />
                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Bar dataKey="users" name="Users" radius={[6, 6, 0, 0]} barSize={40}>
                      {creditExhaustionDistribution.map((_, i) => {
                        const colors = ['#ef4444', '#F87217', '#f59e0b', '#10b981', '#06b6d4', '#8b5cf6'];
                        return <Cell key={i} fill={colors[i] || '#F87217'} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: Users</span>
              <span>X-Axis: Credit Usage Range</span>
            </div>
            <div style={{ color: isDark ? '#64748b' : '#94a3b8' }} className="text-[10px] mt-2 text-center">
              Colour = credit usage level · left = most depleted
            </div>
          </ChartCard>
        </div>
      </div>

      {/* ── Row 3: Low-Rating Reviews Table ─────────────────────────── */}
      <div ref={lowRatingRef}>
        <ChartCard
          title="Recent Low-Rating Reviews"
          subtitle="Reviews rated 1–2 stars · sorted by recency"
          isDark={isDark}
          action={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-red-100 text-red-600 border border-red-200">
                {lowRatingReviews.length} alerts
              </span>
              <DownloadBtn loading={dlKey === 'reviews'} isDark={isDark} onClick={() => dl(lowRatingRef, 'reviews', 'low-rating-reviews')} />
            </div>
          }
        >
          <div
            style={{
              maxHeight: 280, overflowY: 'auto', overflowX: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
            }}
          >
            <DataTable columns={reviewColumns} rows={lowRatingReviews} isDark={isDark} />
          </div>
        </ChartCard>
      </div>

    </div>
  );
};

export default AdminDashboard;